import {
  MasterData,
  MasterDataWithCategory,
  MasterDataWithDisplayType,
  MasterDataWithEnumKey,
  MasterDataWithFieldType,
  MasterDataWithRegionalDivisionType,
} from 'src/models/MasterData'
import { IS_DEVELOPMENT } from 'src/utils/env'
import useSWR, { SWRConfiguration } from 'swr'
import { fetcher } from './client'
import { GET_MASTER_DATA } from './endpoints'
import {
  MasterDataResponse,
  ServerMasterData,
  ServerMasterDataWithCategory,
  ServerMasterDataWithCategoryOnly,
  ServerMasterDataWithDisplayType,
  ServerMasterDataWithEnumKey,
  ServerMasterDataWithFieldType,
  ServerMasterDataWithRegionalDivisionType,
} from './types/masterData'

export type MasterDataStore = {
  positions: MasterData[]
  businessTypes: MasterDataWithCategory[]
  numberOfEmployees: MasterDataWithEnumKey[]
  businessObjects: MasterDataWithEnumKey[]
  needs: MasterDataWithCategory[]
  prefectures: MasterDataWithRegionalDivisionType[]
  capitals: MasterData[]
  features: MasterDataWithCategory[]
  resposibleDomains: MasterData[]
  adminAges: MasterData[]
  foundedAts: MasterData[]
  requestReasonOptions: MasterDataWithDisplayType[]
  capitalNumberOptions: MasterData[]
  positionNumberOptions: MasterData[]
  specialties: MasterDataWithFieldType[]
  budgets: MasterData[]
  introductionTimings: MasterData[]
  solutions: MasterDataWithCategory[]
  contentTags: MasterDataWithCategory[]
  exhibitionTags: MasterDataWithCategory[]
}

const toMasterData = (data: ServerMasterData[]): MasterData[] =>
  data.map((each) => ({
    value: `${each.value}`,
    label: each.label,
  }))

const toMasterDataWithKey = (
  data: ServerMasterDataWithEnumKey[],
): MasterDataWithEnumKey[] =>
  data.map((each) => ({
    value: `${each.value}`,
    label: each.label,
    enumKey: each.enumKey,
  }))

const toMasterDataWithCategory = (
  data: ServerMasterDataWithCategory[],
): MasterDataWithCategory[] =>
  data.map((each) => ({
    value: `${each.value}`,
    label: each.label,
    category: each.categoryType,
    subCategory: each.subCategory,
  }))

// type_of_business用の変換処理（categoryフィールドを使用、subCategoryなし）
const toMasterDataWithCategoryOnly = (
  data: ServerMasterDataWithCategoryOnly[],
): MasterDataWithCategory[] =>
  data.map((each) => ({
    value: `${each.value}`,
    label: each.label,
    category: each.category,
  }))
const toMasterDataWithRegionalDivisionType = (
  data: ServerMasterDataWithRegionalDivisionType[],
): MasterDataWithRegionalDivisionType[] =>
  data.map((each) => ({
    value: `${each.value}`,
    label: each.label,
    regionalDivisionType: each.regionalDivisionType,
  }))
const toMasterDataWithFieldType = (
  data: ServerMasterDataWithFieldType[],
): MasterDataWithFieldType[] =>
  data.map((each) => ({
    value: `${each.value}`,
    label: each.label,
    fieldType: each.fieldType,
  }))
const toMasterDataWithDisplayType = (
  data: ServerMasterDataWithDisplayType[],
): MasterDataWithDisplayType[] =>
  data.map((each) => ({
    value: `${each.value}`,
    label: each.label,
    displayType: each.displayType,
  }))

export const toMasterDataStore = (data: MasterDataResponse) => ({
  positions: toMasterData(data.positions),
  businessTypes: toMasterDataWithCategoryOnly(data.typeOfBusiness),
  numberOfEmployees: toMasterDataWithKey(data.employeeNumbers),
  businessObjects: toMasterDataWithKey(data.businessObjects),
  needs: toMasterDataWithCategory(data.companyBuyNeedOptions),
  prefectures: toMasterDataWithRegionalDivisionType(data.prefectures),
  capitals: toMasterData(data.capitals),
  features: toMasterDataWithCategory(data.features),
  resposibleDomains: toMasterData(data.decidableDivisions),
  requestReasonOptions: toMasterDataWithDisplayType(data.requestReasonOptions),
  foundedAts: toMasterData(data.foundedAts),
  adminAges: toMasterData(data.adminAges),
  capitalNumberOptions: toMasterData(data.capitalNumberOptions),
  positionNumberOptions: toMasterData(data.positionNumberOptions),
  specialties: toMasterDataWithFieldType(data.specialties),
  budgets: toMasterData(data.budgets),
  introductionTimings: toMasterData(data.introductionTimings),
  solutions: toMasterDataWithCategory(data.solutions),
  contentTags: toMasterDataWithCategory(data.contentTags),
  exhibitionTags: toMasterDataWithCategory(data.exhibitionTags),
})

const masterDataFetcher = async (url: string): Promise<MasterDataStore> => {
  const res: MasterDataResponse = await fetcher(url)
  return toMasterDataStore(res)
}

export const useMasterData = (config: Partial<SWRConfiguration> = {}) =>
  useSWR<MasterDataStore>(GET_MASTER_DATA, masterDataFetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: IS_DEVELOPMENT ? undefined : 3600000,
    ...config,
  })

export const fromMasterData = (
  key: string | number | undefined | null,
  masterData: (MasterData | MasterDataWithCategory)[] = [],
) =>
  key != null ? masterData.find(({ value }) => value === `${key}`) : undefined

export const fromMasterDataWithEnumKey = (
  key: string | number | undefined | null,
  masterData: MasterDataWithEnumKey[] = [],
) =>
  key != null
    ? masterData.find(({ enumKey }) => enumKey === `${key}`)
    : undefined

export const fromMasterDataWithRegionalDivisionType = (
  key: string | number | undefined | null,
  masterDataWithRegionalDivisionType: MasterDataWithRegionalDivisionType[],
) =>
  key != null
    ? masterDataWithRegionalDivisionType.find(({ value }) => value === `${key}`)
    : undefined

export const fromMasterDataMultiple = (
  ids: string[] | undefined | null,
  masterData: MasterData[],
): MasterData[] =>
  ids
    ? (ids
        .map((id) => fromMasterData(id, masterData))
        .filter(Boolean) as MasterData[])
    : []
export const fromMasterDataWithCategoryMultiple = (
  ids: string[] | undefined | null,
  masterData: MasterDataWithCategory[],
): MasterDataWithCategory[] =>
  ids
    ? (ids
        .map((id) => fromMasterData(id, masterData))
        .filter(Boolean) as MasterDataWithCategory[])
    : []
